import type { Spec } from "@json-render/react";

import type { JsonRenderRuntimeContextValue } from "@components/Chat/jsonRender/JsonRenderRuntimeContext";
import type { NotificationCardSceneContext } from "@components/Chat/JsonRenderComponents/type";
import {
  parseDynamicUiBody,
  parseDynamicUiTagAttributes,
} from "@utils/parseDynamicUiBody";
import {
  MOVES_SIGNAL_CARD_ELEMENT_TYPES,
  MOVES_SIGNAL_VIZ_ELEMENT_ID,
  MOVES_VIZ_EVENT_TYPE_SET,
  type MovesVizEventType,
} from "./movesSignalCardConstants";
import {
  buildVizSpecFragment,
  type MovesMetrics,
  type MovesSource,
} from "./mapMetricsToVizElement";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type MovesSignalCardBodyContent = "full" | "viz-only";

export type BuildMovesSignalCardSpecInput = {
  eventType?: string;
  recordId?: string;
  created?: number;
  readAt?: number;
  title?: string;
  sceneContext?: NotificationCardSceneContext;
  /** Invest Next moves 列表仅展示 metrics viz + CTA，默认 full 保留完整内容区 */
  bodyContent?: MovesSignalCardBodyContent;
};

export type BuildMovesSignalCardSpecResult = {
  spec: Spec;
  renderContext: Omit<JsonRenderRuntimeContextValue, "scene">;
};

const readMetrics = (value: unknown): MovesMetrics =>
  isRecord(value) ? (value as MovesMetrics) : {};

const readSource = (value: unknown): MovesSource | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const ticker = Array.isArray(value.ticker)
    ? value.ticker.filter((item): item is string => typeof item === "string")
    : typeof value.ticker === "string"
      ? [value.ticker]
      : undefined;
  const theme = Array.isArray(value.theme)
    ? value.theme.filter((item): item is string => typeof item === "string")
    : typeof value.theme === "string"
      ? [value.theme]
      : undefined;
  return { ticker, theme };
};

const resolveEventType = (
  containerProps: Record<string, unknown>,
  fallback?: string,
): MovesVizEventType | undefined => {
  const fromProps = containerProps.eventType;
  const candidate =
    (typeof fromProps === "string" ? fromProps : undefined) ?? fallback;
  if (!candidate || !MOVES_VIZ_EVENT_TYPE_SET.has(candidate)) {
    return undefined;
  }
  return candidate as MovesVizEventType;
};

/** 移除 spec 中所有非 Moves 组件的子元素 */
const stripExistingVizChildren = (
  spec: Spec,
  containerKey: string,
): string[] => {
  const container = spec.elements[containerKey];
  const children = Array.isArray(container?.children) ? container.children : [];

  return children.filter((childId) => {
    const child = spec.elements[childId];
    if (!child || typeof child.type !== "string") {
      return true;
    }
    return !MOVES_SIGNAL_CARD_ELEMENT_TYPES.has(child.type);
  });
};

const serializeMetricsForContext = (
  metrics: MovesMetrics,
): string | undefined => {
  const entries = Object.entries(metrics);
  if (entries.length === 0) {
    return undefined;
  }
  return entries
    .map(([key, value]) => {
      if (typeof value === "string" || typeof value === "number") {
        return `${key}=${value}`;
      }
      return `${key}=${JSON.stringify(value)}`;
    })
    .join(";");
};

const NOTIFICATION_BUTTON_LIST_TYPE = "NotificationButtonList";

/** viz-only：内容区只保留 signal-viz 与 NotificationButtonList */
const filterContainerChildrenForBodyContent = (
  spec: Spec,
  children: string[],
  bodyContent: MovesSignalCardBodyContent,
): string[] => {
  if (bodyContent !== "viz-only") {
    return children;
  }

  return children.filter((childId) => {
    if (childId === MOVES_SIGNAL_VIZ_ELEMENT_ID) {
      return true;
    }
    const child = spec.elements[childId];
    return child?.type === NOTIFICATION_BUTTON_LIST_TYPE;
  });
};

/** 将 TrackerRecord.content 规范为完整 NotificationCardContainer + viz child 结构。 */
export const buildMovesSignalCardSpec = (
  content: string,
  input: BuildMovesSignalCardSpecInput = {},
): BuildMovesSignalCardSpecResult | null => {
  const baseSpec = parseDynamicUiBody(content);
  if (!baseSpec) {
    return null;
  }

  const containerKey = baseSpec.root;
  const container = baseSpec.elements[containerKey];
  if (!container || container.type !== "NotificationCardContainer") {
    return null;
  }

  const containerProps = isRecord(container.props) ? container.props : {};
  const metrics = readMetrics(containerProps.metrics);
  const source = readSource(containerProps.source);
  const eventType = resolveEventType(containerProps, input.eventType);

  const tagAttributes = parseDynamicUiTagAttributes(content);
  const generatedAtMs =
    typeof containerProps.generatedAtMs === "number"
      ? containerProps.generatedAtMs
      : (input.created ?? Date.now());

  const title =
    (typeof containerProps.title === "string"
      ? containerProps.title
      : undefined) ?? input.title;

  let nextElements: Spec["elements"] = { ...baseSpec.elements };
  let nextChildren = stripExistingVizChildren(baseSpec, containerKey);

  if (eventType) {
    const vizFragment = buildVizSpecFragment(eventType, metrics, source);
    if (vizFragment) {
      nextElements = {
        ...nextElements,
        ...vizFragment.elements,
      };
      nextChildren = [MOVES_SIGNAL_VIZ_ELEMENT_ID, ...nextChildren];
    }
  }

  const bodyContent = input.bodyContent ?? "full";
  nextChildren = filterContainerChildrenForBodyContent(
    { root: containerKey, elements: nextElements },
    nextChildren,
    bodyContent,
  );

  nextElements = {
    ...nextElements,
    [containerKey]: {
      ...container,
      props: {
        ...containerProps,
        ...(title ? { title } : {}),
        generatedAtMs,
        ...(eventType ? { eventType } : {}),
      },
      children: nextChildren,
    },
  };

  const spec: Spec = {
    root: containerKey,
    elements: nextElements,
  };

  const renderContext: Omit<JsonRenderRuntimeContextValue, "scene"> = {
    notificationId: tagAttributes.id ?? input.recordId,
    collapsed: tagAttributes.collapsed ?? false,
    metrics: tagAttributes.metrics ?? serializeMetricsForContext(metrics),
    isRead: input.readAt != null && input.readAt > 0,
    title,
    generatedAtMs,
    ...(input.sceneContext ? { sceneContext: input.sceneContext } : {}),
  };

  return { spec, renderContext };
};
